import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import logo from "../assets/images/logo.webp";
import { RiMenu4Line } from "react-icons/ri";
import { FaPhone } from "react-icons/fa6";
import {
  doc,
  onSnapshot,
  getDoc,
  deleteDoc,
  updateDoc,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { supabase } from "../../supabase";
import driver from "../assets/images/driver.png";
import { toast } from "react-toastify";
import { formatINR } from "../../utils/currency";
import { GST } from "../../API_KEYS";
import Loader from "../Loader";

function DriverBooking() {
  const { id } = useParams();

  const [booking, setBooking] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(null);

  const [drivers, setDrivers] = useState(null);
  const [showEndModal, setShowEndModal] = useState(false);

  const [meterReadingInput, setMeterReadingInput] = useState(["", "", "", ""]);

  /* -------------------------------------------------- */
  /*  FETCH BOOKING + CUSTOMER                         */
  /* -------------------------------------------------- */

  useEffect(() => {
    if (!id) return;

    const unsubscribe = onSnapshot(
      doc(db, "LiveBookings", id),
      async (snap) => {
        if (!snap.exists()) return;

        const bookingData = snap.data();
        setBooking(bookingData);

        // Fetch customer
        if (bookingData.customer_id) {
          const userSnap = await getDoc(
            doc(db, "Users", bookingData.customer_id),
          );
          if (userSnap.exists()) {
            setCustomer(userSnap.data());
          }
        }

        // Fetch vehicle if assigned
        if (bookingData.vehicle_id) {
          const { data } = await supabase
            .from("Vehicles")
            .select("*")
            .eq("id", bookingData.vehicle_id)
            .single();

          if (data) setVehicle(data);
        } else {
          setVehicle(null);
        }

        // Fetch driver if assigned
        if (bookingData.driver_id) {
          const { data } = await supabase
            .from("Drivers")
            .select("*")
            .eq("id", bookingData.driver_id)
            .single();

          if (data) setDrivers(data);
        } else {
          setDrivers(null);
        }
      },
    );

    return () => unsubscribe();
  }, [id]);

  /* -------------------------------------------------- */

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp.seconds * 1000).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const [acceptloader, setAcceptLoader] = useState(false);

  const handleAcceptBooking = async () => {
    setAcceptLoader(true);
    try {
      await updateDoc(doc(db, "LiveBookings", id), {
        status: 2,
        accepted_at: serverTimestamp(),
      });

      toast.success("Ride Accepted!");
      setAcceptLoader(false);
    } catch (error) {
      setAcceptLoader(false);
      console.error("Error updating booking:", error);
      toast.error("Something went wrong");
    }
  };

  const [startMeterRead, setStartMeterRead] = useState(["", "", "", ""]);

  const handleStartMeterChange = (value, index) => {
    if (!/^\d?$/.test(value)) return; // allow only single digit

    const newRead = [...startMeterRead];
    newRead[index] = value;
    setStartMeterRead(newRead);

    // Move to next input automatically
    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const HandleStartRide = async () => {
    if (startMeterRead.some((digit) => digit === "")) {
      toast.error("Enter complete Meter Reading");
      return;
    }

    try {
      const meterReadingString = startMeterRead.join(""); // 🔥 convert to string

      await updateDoc(doc(db, "LiveBookings", id), {
        status: 3,
        startMeterRead: meterReadingString, // now saved as string
        ride_start: serverTimestamp(), // better than new Date()
      });

      toast.success("Ride Started!");
    } catch (error) {
      console.error(error);
    }
  };

  const handleMeterChange = (value, index) => {
    if (!/^\d?$/.test(value)) return; // allow only single digit

    const newMeter = [...meterReadingInput];
    newMeter[index] = value;
    setMeterReadingInput(newMeter);

    // 👉 Move forward
    if (value && index < meterReadingInput.length - 1) {
      const nextInput = document.getElementById(`meter-${index + 1}`);
      nextInput?.focus();
    }

    // 👉 Move backward when deleting
    if (!value && index > 0) {
      const prevInput = document.getElementById(`meter-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleEndTrip = async () => {
    const meterReading = meterReadingInput.join("");

    if (booking.isSchedule && meterReading.length !== 4) {
      toast.error("Please enter complete meter reading");
      return;
    }

    try {
      await updateDoc(doc(db, "LiveBookings", id), {
        status: 4,
        ride_end: new Date(),
        meter_reading: parseInt(meterReading),
        // NoOfDays: calculateRideDays(booking.ride_start, booking.ride_end),
      });

      toast.success("Trip Ended Successfully!");
      setShowEndModal(false);
    } catch (error) {
      console.error("Error ending trip:", error);
      toast.error("Failed to end trip");
    }
  };

  const calculateRideDays = (start, end) => {
    if (!start || !end) return 0;

    // Convert Firestore Timestamp to JS Date
    const startDate = start?.seconds
      ? new Date(start.seconds * 1000)
      : new Date(start);

    const endDate = end?.seconds ? new Date(end.seconds * 1000) : new Date(end);

    const diffInMs = endDate - startDate;

    if (diffInMs <= 0) return 0;

    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

    return diffInDays;
  };

  const days = calculateRideDays(booking?.ride_start, booking?.ride_end);

  const dailyRate = Number(booking?.daily_rate || 0);
  const kmPrice = Number(booking?.km_price || 0);
  const meter = Number(booking?.meter_reading || 0);
  const advance = Number(booking?.preBookingFee || 0);
  const totalPrice = Number(booking?.total_price || 0);

  let finalAmount = 0;

  if (booking?.booking_type === "Reserve") {
    const base = days * dailyRate + kmPrice * meter;
    const gstAmount = ((base + advance) * GST) / 100;

    finalAmount = base + gstAmount - advance; // ✅ correct subtraction
  } else {
    finalAmount = totalPrice + (totalPrice * GST) / 100;
  }

  const handleConfirmEndTrip = async (booking) => {
    if (!window.confirm("Are you sure you have recieved the payment?")) {
      return;
    }

    try {
      // Prepare booking data for Supabase
      const bookingData = {
        NoOfDays: calculateRideDays(booking.ride_start, booking.ride_end) || 0,
        booking_type: booking.booking_type || "",
        accepted_at: booking.accepted_at
          ? new Date(booking.accepted_at.seconds * 1000).toISOString()
          : new Date().toISOString(),
        created_at: booking.created_at
          ? new Date(booking.created_at.seconds * 1000).toISOString()
          : new Date().toISOString(),
        customer_id: booking.customer_id || "",
        customer_name: booking.customer_name || "",
        customer_phone: booking.customer_phone || "",
        daily_rate: parseFloat(booking.daily_rate || 0),
        driver_id: booking.driver_id || 0,
        drop: booking.drop || "",
        dropState: booking.dropState || "",
        isScheduled: booking.isScheduled || false,
        km_price: parseInt(booking.km_price || 0),
        passengers: booking.passengers || 1,
        payment_status: booking.payment_status || "notpaid",
        pickup: booking.pickup || "",
        pickupTime: booking.pickupTime
          ? new Date(booking.pickupTime.seconds * 1000).toISOString()
          : null,
        preBookingFee: booking.preBookingFee || 0,
        preBookingFeePaid: booking.preBookingFeePaid || false,
        razorpay_payment_ref: booking.razorpay_payment_ref || "",
        status: 5,
        total_paid_amount: booking.total_paid_amount || 0,
        total_paid_at: booking.total_paid_at
          ? new Date(booking.total_paid_at.seconds * 1000).toISOString()
          : null,
        total_payment_id: booking.total_payment_id || "",
      
        veh_color: booking.veh_color || "",
        veh_number: booking.veh_number || "",
        vehicle_id: booking.vehicle_id || 0,

        total_price: parseFloat(booking.total_price || 0),
        gst: GST || 5, //gst percent

        confirm_price: booking.confirm_price || 0,

        meter_reading: booking.meter_reading || "0",
 
        ride_start: booking.ride_start
          ? new Date(booking.ride_start.seconds * 1000).toISOString()
          : null,
        ride_end: booking.ride_end
          ? new Date(booking.ride_end.seconds * 1000).toISOString()
          : new Date().toISOString(),
      };

      // 🔥 Insert into Supabase
      const { error } = await supabase.from("Bookings").insert([bookingData]);

      if (error) {
        console.error("Supabase insert error:", error);
        toast.error("Failed to complete trip");
        return;
      }

      // 🔥 Delete from Firestore LiveBookings
      await deleteDoc(doc(db, "LiveBookings", id));

      toast.success("Trip Completed Successfully!");
      setTimeout(() => {
        window.location.replace("/");
      }, 2000);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  if (!booking) return <Loader />;
  return (
    <>
      <nav className="navbar navbar-expand-lg fixed-top">
        <div className="container">
          <Link className="admin-brand" to="/admin-live-bookings">
            <img src={logo} />
          </Link>

          <RiMenu4Line
            className="navbar-menu-bar me-2"
            data-bs-toggle="collapse"
            data-bs-target="#navbarSupportedContent"
          />
        </div>
      </nav>

      <div className="container" style={{ padding: "20px", marginTop: "67px" }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="heading-title mb-0">Booking Details</h5>
          {drivers ? (
            <div className="d-flex gap-2 align-items-center">
              <img
                src={drivers.profile_img || driver}
                width={25}
                height={25}
                style={{
                  borderRadius: "4px",
                  objectFit: "cover",
                }}
                alt="driver"
              />

              <p className="mb-0">{drivers.name}</p>
            </div>
          ) : null}
        </div>

        <div className="card highlight-card shadow-sm p-3 mb-2">
          <p className="mb-0">
            Pick up: <b> {booking.pickup}</b>
          </p>
          <p className="mb-0">
            Drop: <b>{booking.drop}</b>
          </p>
          <p className="mb-0">
            Pick-up Time:{" "}
            <b>
              {formatTimestamp(booking.pickupTime) ||
                formatTimestamp(booking.created_at)}
            </b>
          </p>
        </div>

        <div className="card highlight-card shadow-sm p-3 mb-2">
          {vehicle ? (
            <div className="d-flex gap-2 align-items-center justify-content-between">
              <div>
                <p className="mb-0">
                  Vehicle:{" "}
                  <b>
                    <span style={{ textTransform: "capitalize" }}>
                      {booking.veh_color}
                    </span>{" "}
                    {vehicle.name}
                  </b>
                </p>
                <p className="mb-0">
                  Number: <b>{booking.veh_number}</b>
                </p>
                <p className="mb-0">
                  Fuel: <b>{vehicle.fuel}</b>
                </p>
              </div>
              <img src={vehicle.img_url} alt="vehicle" width={110} />
            </div>
          ) : (
            <p className="mb-1 text-center">
              Customer Vehicle Preference:{" "}
              <b style={{ textTransform: "capitalize" }}>
                {booking.vehicle_type}
              </b>
            </p>
          )}
        </div>
        {booking.status <= 3 ? (
          <>
            <div className="card highlight-card shadow-sm p-3 mb-2">
              {booking.booking_type == "Reserve" ? (
                <>
                  <div className="row">
                    <div className="col-6">
                      <p className="mb-0">No of Days:</p>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                      <b className="mb-0">{booking.NoOfDays} Days</b>
                    </div>
                    <div className="col-6">
                      <p className="mb-0">KM Rate:</p>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                      <b className="mb-0">{formatINR(booking.km_price)}</b>
                    </div>
                    <div className="col-6">
                      <p className="mb-0">Daily Rate:</p>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                      <b className="mb-0">{formatINR(booking.daily_rate)}</b>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="row">
                    <div className="col-6">
                      <p className="mb-0">Total:</p>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                      <b className="mb-0" style={{ color: "green" }}>
                        {formatINR(
                          booking.total_price +
                            (booking.total_price * GST) / 100,
                        )}
                      </b>
                      <p
                        className="mb-0"
                        style={{ fontSize: "10px", color: "grey" }}
                      >
                        ( Including GST )
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        ) : null}

        {booking.status === 2 ? (
          <div className="card highlight-card shadow-sm p-3 mb-2">
            <p className="mb-2">Enter Meter Reading</p>
            <div className="d-flex gap-2 justify-content-center">
              {startMeterRead.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) =>
                    handleStartMeterChange(e.target.value, index)
                  }
                  className="form-control text-center"
                  style={{ width: "50px", fontSize: "20px" }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !digit && index > 0) {
                      const prevInput = document.getElementById(
                        `otp-${index - 1}`,
                      );
                      prevInput?.focus();
                    }
                  }}
                />
              ))}
            </div>

            <button
              onClick={HandleStartRide}
              className="btn btn-dark mt-2 w-100"
            >
              Start Ride
            </button>
          </div>
        ) : null}

        <div className="card highlight-card shadow-sm p-3 mb-2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <p className="mb-0">
                Passenger Name: <b>{customer?.name || booking.customer_name}</b>
              </p>
              <p className="mb-0">
                Passenger Phone: <b>+91 **********</b>
              </p>
              <p className="mb-0">
                Passengers: <b>{booking.passengers}</b>
              </p>
            </div>
            <a
              href={`tel:${booking.customer?.phone || booking.customer_phone}`}
              className="btn btn-dark"
            >
              <FaPhone className="icon" />
            </a>
          </div>
        </div>

        {booking.status === 1 ? (
          <>
            <button
              disabled={acceptloader}
              onClick={handleAcceptBooking}
              className="btn btn-dark mt-3 w-100"
            >
              {acceptloader ? "Accepting..." : "Accept Booking"}
            </button>
          </>
        ) : booking.status === 3 ? (
          <>
            <div className="card highlight-card shadow-sm p-3 mb-2">
              <div className="d-flex justify-content-between">
                <small className="text-muted">Ride Started</small>
                <small className="fw-semibold" style={{ color: "green" }}>
                  {formatTimestamp(booking.ride_start) || "--"}
                </small>
              </div>
            </div>
            <div className="card highlight-card shadow-sm p-3 mb-2">
              {booking.isScheduled ? (
                <>
                  <p className="mb-2">Enter Final Meter Reading</p>
                  <div className="d-flex gap-2 mb-3 justify-content-center">
                    {meterReadingInput.map((digit, index) => (
                      <input
                        key={index}
                        id={`meter-${index}`}
                        type="text"
                        maxLength="1"
                        value={digit}
                        placeholder="0"
                        onChange={(e) =>
                          handleMeterChange(e.target.value, index)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !digit && index > 0) {
                            const prevInput = document.getElementById(
                              `meter-${index - 1}`,
                            );
                            prevInput?.focus();
                          }
                        }}
                        className="form-control text-center"
                        style={{ width: "50px", fontSize: "20px" }}
                      />
                    ))}
                  </div>
                </>
              ) : null}

              <button
                onClick={() => setShowEndModal(true)}
                className="btn btn-danger w-100"
              >
                End Trip
              </button>
            </div>
          </>
        ) : null}

        {booking.status >= 4 ? (
          <>
            <div className="card highlight-card shadow-sm p-3 mb-2">
              <p className="mb-1">
                <b>Ride Summary</b>
              </p>
              <div className="row">
                <div className="col-4">
                  <small style={{ fontSize: "12px" }}>Ride Started :</small>
                </div>
                <div style={{ textAlign: "right" }} className="col-8">
                  <b style={{ fontSize: "14px" }}>
                    {formatTimestamp(booking.ride_start) || ""}
                  </b>
                </div>
                <div className="col-4">
                  <small style={{ fontSize: "12px" }}>Ride Ended :</small>
                </div>
                <div style={{ textAlign: "right" }} className="col-8">
                  <b style={{ fontSize: "14px" }}>
                    {formatTimestamp(booking.ride_end) || ""}
                  </b>
                </div>
              </div>
              <hr />
              <div className="row">
                {booking.booking_type == "Reserve" ? (
                  <>
                    <div className="col-8">
                      <small className="mb-0" style={{ fontSize: "12px" }}>
                        Daily Charge{" "}
                        <i className="mb-0">
                          ({" "}
                          {calculateRideDays(
                            booking.ride_start,
                            booking.ride_end,
                          )}{" "}
                          Day(s) x ₹{booking.daily_rate} )
                        </i>{" "}
                        :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <b style={{ fontSize: "14px" }}>
                        {formatINR(
                          calculateRideDays(
                            booking.ride_start,
                            booking.ride_end,
                          ) * booking.daily_rate,
                        )}
                      </b>
                    </div>
                    <div className="col-8">
                      <small style={{ fontSize: "12px" }}>
                        Total KM Rate{" "}
                        <i>
                          ( ₹{booking.km_price} x {booking.meter_reading} )
                        </i>
                        :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <b style={{ fontSize: "14px" }}>
                        {formatINR(booking.km_price * booking.meter_reading)}
                      </b>
                    </div>
                    <div className="col-8">
                      <small className="mb-0" style={{ fontSize: "12px" }}>
                        GST <i>( {GST}% )</i> :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <b style={{ fontSize: "14px" }}>
                        {formatINR(
                          ((calculateRideDays(
                            booking.ride_start,
                            booking.ride_end,
                          ) *
                            booking.daily_rate +
                            booking.km_price * booking.meter_reading +
                            booking.preBookingFee) *
                            GST) /
                            100,
                        )}
                      </b>
                    </div>

                    <div className="col-8">
                      <small style={{ fontSize: "12px" }}>
                        Advance Amount :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <i style={{ fontSize: "14px" }}>
                        - {formatINR(booking.preBookingFee)}
                      </i>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-8">
                      <small className="mb-0" style={{ fontSize: "12px" }}>
                        Ride Charge :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <b style={{ fontSize: "14px" }}>
                        {formatINR(booking.total_price)}
                      </b>
                    </div>

                    <div className="col-8">
                      <small className="mb-0" style={{ fontSize: "12px" }}>
                        GST <i>( 5% )</i> :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <b style={{ fontSize: "14px" }}>
                        {formatINR((booking.total_price * GST) / 100)}
                      </b>
                    </div>
                  </>
                )}

                <div className="col-6">
                  <small style={{ fontSize: "15px" }}>Grand Total :</small>
                </div>
                <div style={{ textAlign: "right" }} className="col-6">
                  <b style={{ fontSize: "22px", color: "green" }}>
                    {formatINR(finalAmount)}
                  </b>
                </div>
              </div>
            </div>
          </>
        ) : null}

        {booking.status == 4 ? (
          <>
            <button
              onClick={() => handleConfirmEndTrip(booking)}
              className="btn btn-success w-100"
            >
              {booking.payment_status == "paid"
                ? "Finish Trip"
                : "Cash Recieved"}
            </button>
          </>
        ) : null}
      </div>

      {showEndModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">End Trip</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEndModal(false)}
                ></button>
              </div>

              <div className="modal-body">
                <p>Are you sure you want to end this trip?</p>
                <p className="text-muted" style={{ fontSize: "14px" }}>
                  This action cannot be undone.
                </p>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowEndModal(false)}
                >
                  Cancel
                </button>

                <button className="btn btn-danger" onClick={handleEndTrip}>
                  Yes, End Trip
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DriverBooking;
